import { FunctionComponent } from 'react';
import styled from 'styled-components';
import CommunityTablePagination from '../community/CommunityTablePagination';
import {
  ActionButton,
  ActionButtons,
  BodyCell,
  CellText,
  EmptyIcon,
  EmptyText,
  HeaderCell,
  HeaderText,
  RetryButton,
  Spinner,
  StateIcon,
  StateText,
  TableBody,
  TableContainer,
  TableHeader,
  TableHeaderRow,
  TableInner,
  TableRow,
  TableState,
} from '../common/AdminTablePrimitives';
import { CoffeeChatQuestionData, CoffeeChatPaginationState } from '../../../services/admin/adminCoffeeChatApi';

const StatusText = styled.span<{ $answered: boolean }>`
  font-size: 16px;
  line-height: 150%;
  color: ${({ $answered }) => ($answered ? '#296768' : 'rgba(55, 56, 60, 0.61)')};
  white-space: nowrap;
`;

interface CoffeeChatQuestionTableProps {
  questions: CoffeeChatQuestionData[];
  loading: boolean;
  error: string | null;
  pagination: CoffeeChatPaginationState;
  onDelete: (questionId: number) => void;
  onToggleVisibility: (questionId: number, currentVisible: boolean) => void;
  onManageReply?: (questionId: number) => void;
  onDeleteAnswer?: (question: CoffeeChatQuestionData) => void;
  onRetry?: () => void;
  onPageChange: (page: number) => void;
}

const CoffeeChatQuestionTable: FunctionComponent<CoffeeChatQuestionTableProps> = ({
  questions,
  loading,
  error,
  pagination,
  onDelete,
  onToggleVisibility,
  onManageReply,
  onDeleteAnswer,
  onRetry,
  onPageChange,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date
      .toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
      .replace(/\. /g, '.')
      .replace(/\.$/, '');
  };

  const formatTitle = (title: string) => (title && title.length > 50 ? `${title.substring(0, 50)}...` : title || '-');

  const isInitialLoading = loading && questions.length === 0;
  if (isInitialLoading) {
    return (
      <TableContainer>
        <TableState>
          <Spinner />
          <StateText>커피챗 질문 목록을 불러오는 중입니다.</StateText>
        </TableState>
      </TableContainer>
    );
  }

  if (error && questions.length === 0) {
    return (
      <TableContainer>
        <TableState>
          <StateIcon>안내</StateIcon>
          <StateText>{error}</StateText>
          {onRetry && <RetryButton onClick={onRetry}>다시 시도</RetryButton>}
        </TableState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <TableInner>
        <TableHeader>
          <TableHeaderRow $columns="60px minmax(150px, 2.4fr) minmax(60px,1fr) 76px minmax(60px,1fr) 110px 120px 120px">
            <HeaderCell><HeaderText>연번</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>제목</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>멘토</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>상태</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>작성자</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>작성일</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>게시글</HeaderText></HeaderCell>
            <HeaderCell><HeaderText>답변</HeaderText></HeaderCell>
          </TableHeaderRow>
        </TableHeader>

        <TableBody>
          {questions.length === 0 ? (
            <TableState>
              <EmptyIcon>∅</EmptyIcon>
              <EmptyText>등록된 커피챗 질문이 없습니다.</EmptyText>
            </TableState>
          ) : (
            questions.map((question, index) => (
              <TableRow key={question.id} $columns="60px minmax(150px, 2.4fr) minmax(60px,1fr) 76px minmax(60px,1fr) 110px 120px 120px">
                <BodyCell>
                  <CellText>{pagination.totalElements - pagination.page * pagination.size - index}</CellText>
                </BodyCell>
                <BodyCell>
                  <CellText title={question.title}>{formatTitle(question.title)}</CellText>
                </BodyCell>
                <BodyCell>
                  <CellText>{question.mentor_name}</CellText>
                </BodyCell>
                <BodyCell>
                  <StatusText $answered={question.is_answered}>{question.is_answered ? '답변 완료' : '답변 전'}</StatusText>
                </BodyCell>
                <BodyCell>
                  <CellText>{question.writer_name}</CellText>
                </BodyCell>
                <BodyCell>
                  <CellText>{formatDate(question.created_at)}</CellText>
                </BodyCell>
                <BodyCell>
                  <ActionButtons>
                    <ActionButton type="button" $tone="danger" onClick={() => onDelete(question.id)}>
                      삭제
                    </ActionButton>
                    <ActionButton type="button" $tone="default" onClick={() => onToggleVisibility(question.id, question.visible)}>
                      {question.visible ? '숨기기' : '노출'}
                    </ActionButton>
                  </ActionButtons>
                </BodyCell>
                <BodyCell>
                  <ActionButtons>
                    <ActionButton type="button" $tone="default" onClick={() => onManageReply?.(question.id)}>
                      {question.is_answered ? '수정' : '답변'}
                    </ActionButton>
                    {question.is_answered && (
                      <ActionButton type="button" $tone="danger" onClick={() => onDeleteAnswer?.(question)}>
                        삭제
                      </ActionButton>
                    )}
                  </ActionButtons>
                </BodyCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </TableInner>
      <CommunityTablePagination
        currentPage={pagination.page + 1}
        totalPages={pagination.totalPages}
        onPageChange={(page) => onPageChange(page - 1)}
      />
    </TableContainer>
  );
};

export default CoffeeChatQuestionTable;
